import { useTriggerEventContext } from '@components/utils/contexts/TriggerEventContext';
import { TrackEvent } from '@enums';
import { Enum_Modals_Trigger } from '@graphql/generated/graphql';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import { AnimationControls } from 'framer-motion';
import { useRouter } from 'next/router';
import {
    createContext,
    Dispatch,
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useReducer,
} from 'react';
import { useCounter } from 'react-use';

import { useStoryContext } from './StoryContext';
import { getValidPageIndex } from '../utils/getValidPageIndex';

interface ProgressContext {
    isPaused: boolean;
    numberOfPages: number;
    currentPageIndex: number;
    animationControls?: AnimationControls;
    pageDuration?: number;
    startAnimation?: (duration?: number, isInfinite?: boolean) => void;
    pauseAnimation?: () => void;
    resumeAnimation?: () => void;
    resetAnimation?: () => void;
    loopCountInc: () => void;
}

export interface ProgressContextProps extends PropsWithChildren {
    pageIndex?: number;
}

type ProgressAction =
    | { type: 'START'; payload?: { duration: number; isInfinite?: boolean } }
    | { type: 'PAUSE'; payload?: { animationOnly?: boolean } }
    | { type: 'RESUME' }
    | { type: 'RESET' }
    | { type: 'RESTART' }
    | { type: 'SET_PAGE'; payload: { number: number } }
    | { type: 'PREVIOUS_PAGE' }
    | { type: 'NEXT_PAGE' }
    | {
          type: 'SET_ANIMATIONS';
          payload: {
              startAnimation: (duration?: number, isInfinite?: boolean) => void;
              pauseAnimation: () => void;
              resumeAnimation: () => void;
              resetAnimation: () => void;
          };
      };
const progressReducer = (state: ProgressContext, action: ProgressAction) => {
    switch (action.type) {
        case 'START':
            state.startAnimation?.(
                action.payload?.duration,
                action.payload?.isInfinite
            );
            return {
                ...state,
                isPaused: false,
                pageDuration: action.payload?.duration,
            };

        case 'PAUSE':
            state.pauseAnimation?.();
            if (action.payload?.animationOnly) return state;
            return { ...state, isPaused: true };

        case 'RESUME':
            state.resumeAnimation?.();
            return { ...state, isPaused: false };

        case 'RESET': {
            state.resetAnimation?.();
            return state;
        }

        case 'RESTART': {
            state.loopCountInc();
            return state;
        }

        case 'SET_PAGE':
            return {
                ...state,
                currentPageIndex: action.payload.number,
            };

        case 'PREVIOUS_PAGE':
            return {
                ...state,
                currentPageIndex: Math.max(state.currentPageIndex - 1, 0),
            };

        case 'NEXT_PAGE':
            return {
                ...state,
                currentPageIndex: Math.min(
                    state.currentPageIndex + 1,
                    state.numberOfPages - 1
                ),
            };

        case 'SET_ANIMATIONS':
            return {
                ...state,
                startAnimation: action.payload.startAnimation,
                pauseAnimation: action.payload.pauseAnimation,
                resumeAnimation: action.payload.resumeAnimation,
                resetAnimation: action.payload.resetAnimation,
            };

        default:
            return state;
    }
};

export const ProgressContext = createContext<ProgressContext | null>(null);

export const ProgressDispatchContext = createContext<Dispatch<ProgressAction>>(
    () => {}
);

export const ProgressProvider: FC<ProgressContextProps> = ({
    children,
    pageIndex,
}) => {
    const { track } = useAnalytics();
    const { onTriggerEvent } = useTriggerEventContext();
    const router = useRouter();
    const { story, getActiveStoryPages } = useStoryContext();

    const [, { inc: loopCountInc, reset: loopCountReset, get: loopCountGet }] =
        useCounter(0);

    // Get the current active story pages count
    const activeStoryPages = getActiveStoryPages();
    const currentNumberOfPages = activeStoryPages.length;

    const initialContext: ProgressContext = {
        currentPageIndex: getValidPageIndex({
            numberOfPages: currentNumberOfPages,
            index: pageIndex,
        }),
        isPaused: false,
        numberOfPages: currentNumberOfPages,
        loopCountInc,
    };

    const [state, dispatch] = useReducer(progressReducer, {
        ...initialContext,
    });

    // Update numberOfPages when active story pages change
    useEffect(() => {
        if (state.numberOfPages !== currentNumberOfPages) {
            dispatch({
                type: 'SET_PAGE',
                payload: {
                    number: Math.min(
                        state.currentPageIndex,
                        currentNumberOfPages - 1
                    ),
                },
            });
        }
    }, [currentNumberOfPages, state.currentPageIndex, state.numberOfPages]);

    useEffect(() => {
        if (pageIndex) {
            dispatch({
                type: 'SET_PAGE',
                payload: {
                    number: getValidPageIndex({
                        numberOfPages: currentNumberOfPages,
                        index: pageIndex,
                    }),
                },
            });
        }
    }, [currentNumberOfPages, pageIndex]);

    const handleRouteChange = useCallback(
        (targetUrl: string) => {
            if (
                router.asPath.includes('onboarding') &&
                !targetUrl.includes('onboarding')
            ) {
                track(TrackEvent.ExitOnboarding, {
                    current_step: state.currentPageIndex + 1,
                    total_steps: currentNumberOfPages,
                });
            }
        },
        [router.asPath, state.currentPageIndex, currentNumberOfPages, track]
    );

    useEffect(() => {
        router.events.on('routeChangeStart', handleRouteChange);
        return () => router.events.off('routeChangeStart', handleRouteChange);
    }, [router.events, handleRouteChange]);

    useEffect(() => {
        if (!story) return;

        const t_init = performance.now();

        return () => {
            track(TrackEvent.StoryPageView, {
                story_title: story.Title,
                index: state.currentPageIndex,
                page_title:
                    activeStoryPages[state.currentPageIndex]?.subtitle || '',
                total_pages: currentNumberOfPages,
                video_duration: state.pageDuration || 0,
                view_duration: Math.round(performance.now() - t_init),
                loop_count: loopCountGet(),
            });
            loopCountReset();
        };
    }, [state.currentPageIndex]);

    useEffect(() => {
        if (!story) return;

        onTriggerEvent({
            event: Enum_Modals_Trigger.DisplayStoryPage,
            filters: {
                title: activeStoryPages[state.currentPageIndex]?.subtitle || '',
                storySlug: story.Slug,
            },
        });

        if (state.currentPageIndex === currentNumberOfPages - 1) {
            onTriggerEvent({
                event: Enum_Modals_Trigger.LastStoryPage,
                filters: { storySlug: story.Slug },
            });
        }

        if (state.currentPageIndex === 0) {
            onTriggerEvent({
                event: Enum_Modals_Trigger.FirstStoryPage,
                filters: { storySlug: story.Slug },
            });
        }
    }, [
        story,
        state.currentPageIndex,
        currentNumberOfPages,
        activeStoryPages,
        onTriggerEvent,
    ]);

    return (
        <ProgressContext.Provider
            value={{ ...state, numberOfPages: currentNumberOfPages }}
        >
            <ProgressDispatchContext.Provider value={dispatch}>
                {children}
            </ProgressDispatchContext.Provider>
        </ProgressContext.Provider>
    );
};

export const useProgressContext = () => {
    const context = useContext(ProgressContext);
    if (!context) {
        throw new Error(
            'useProgressContext must be used within a ProgressProvider'
        );
    }
    return context;
};

export const useProgressDispatchContext = () => {
    const dispatch = useContext(ProgressDispatchContext);
    if (!dispatch) {
        throw new Error(
            'useProgressDispatchContext must be used within a ProgressProvider'
        );
    }
    return dispatch;
};
